import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Collapse,
  Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import authService from '../services/authService';
import apiClient from '../services/apiClient';

const Profile = () => {
  const navigate = useNavigate();
  const student = authService.getCurrentStudent();
  
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [staffInfo, setStaffInfo] = useState({ teacher: null, curator: null, group: '' });
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [showTeacher, setShowTeacher] = useState(true);
  const [showCurator, setShowCurator] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Fetch staff info and stats
  useEffect(() => {
    if (!student) {
      navigate('/login');
      return;
    }

    const fetchStaffInfo = async () => {
      try {
        const response = await apiClient.get('/Students/my-staff');
        setStaffInfo(response.data);
      } catch (err) {
        console.error('Error fetching staff info:', err);
      } finally {
        setLoadingStaff(false);
      }
    };

    fetchStaffInfo();
  }, [student, navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 4) {
      setError('New password must be at least 4 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    if (oldPassword === newPassword) {
      setError('New password must be different from old password');
      return;
    }

    setLoading(true);

    try {
      await apiClient.put('/Students/change-password', {
        oldPassword,
        newPassword
      });

      setSuccess('Password changed successfully! You will be logged out in 2 seconds...');
      setShowSuccessToast(true);
      
      // Clear form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Logout and redirect after 2 seconds
      setTimeout(() => {
        authService.logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Password change error:', err);
      const message = err.response?.data || err.message || 'Failed to change password';
      setError(typeof message === 'string' ? message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!student) {
    return null;
  }

  const studentName = student.name || 'Student';
  const studentCode = student.studentCode || '';
  const initials = studentName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Calculate profile completion
  const profileCompletion = () => {
    let score = 0;
    if (studentName) score += 30;
    if (studentCode) score += 20;
    if (staffInfo.teacher) score += 25;
    if (staffInfo.curator) score += 25;
    return score;
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f7fa',
      py: 4
    }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                fontSize: 32,
                fontWeight: 'bold',
                bgcolor: 'primary.main',
                mr: 3
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h4" gutterBottom>
                {studentName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Student ID: {studentCode}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Profile Completion
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={profileCompletion()} 
                  sx={{ width: 100, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" fontWeight="bold">
                  {profileCompletion()}%
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Profile Completion Card */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                <Typography variant="h4">{profileCompletion()}%</Typography>
                <Typography variant="caption" color="text.secondary">Profile Complete</Typography>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
            <Tab label="Profile Information" />
            <Tab label="Security" />
          </Tabs>

          {/* Tab 0: Profile Information */}
          {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Profile Information</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Name: <strong>{studentName}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Student ID: <strong>{studentCode}</strong>
                    </Typography>
                    {staffInfo.group && (
                      <Typography variant="body2" color="text.secondary">
                        Group: <strong>{staffInfo.group}</strong>
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Teacher Card - Collapsible */}
            {!loadingStaff && staffInfo.teacher && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">Your Teacher</Typography>
                      <IconButton size="small" onClick={() => setShowTeacher(!showTeacher)}>
                        {showTeacher ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={showTeacher}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Avatar
                          src={staffInfo.teacher.profileImageUrl}
                          sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.light' }}
                        >
                          {staffInfo.teacher.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {staffInfo.teacher.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Teacher
                          </Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Curator Card - Collapsible */}
            {!loadingStaff && staffInfo.curator && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">Your Curator</Typography>
                      <IconButton size="small" onClick={() => setShowCurator(!showCurator)}>
                        {showCurator ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={showCurator}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Avatar
                          src={staffInfo.curator.profileImageUrl}
                          sx={{ width: 60, height: 60, mr: 2, bgcolor: 'secondary.light' }}
                        >
                          {staffInfo.curator.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {staffInfo.curator.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Curator
                          </Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
          )}

          {/* Tab 1: Security */}
          {activeTab === 1 && (
          <Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleChangePassword}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  disabled={loading}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          edge="end"
                        >
                          {showOldPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  required
                  helperText="Must be at least 4 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
          </Box>
          )}
        </Paper>

        {/* Success Toast */}
        <Snackbar 
          open={showSuccessToast} 
          autoHideDuration={6000} 
          onClose={() => setShowSuccessToast(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setShowSuccessToast(false)}>
            Password changed successfully! Logging out...
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Profile;
